import React from "react";
import { Container, Row, Col, Button } from "reactstrap";
import { Link } from "react-router-dom";
import { trackGAEvent } from "../../common/analytics";
import { getBasePath, getStoreBrandName, getStoreLogoPath } from "../../utils";

const highlights = [
  {
    icon: "fa-solid fa-cube",
    title: "Top Graded Plywood",
    desc: "IS:303 & IS:710 — dense, stable, built to hold for years.",
  },
  {
    icon: "fa-solid fa-ruler-combined",
    title: "Snug-Fit Precision",
    desc: "Multiple sizes, made for Indian homes. No gaps, no wasted space, no guesswork.",
  },
  {
    icon: "fa-solid fa-circle-check",
    title: "Edges That Stay Clean",
    desc: "Laminates and edge-banding applied uniformly — no peeling, no chipping, no touch-ups.",
  },
  {
    icon: "fa-solid fa-medal",
    title: "Warranty that Stands",
    desc: "7–10 year warranty on every unit. 10 day replacement guarantee.",
  },
];

const featureSections = [
  {
    title: "Order carcass just like hardwares",
    content: "No cutlist, no mistakes, no delays, no waiting.",
    image: "buy_using_mobile.webp", // Image: Person using a laptop with the ZeroBuild catalog UI
    keyPoints: [
      "🧑‍💼 Simple Ordering",
      "🖱️ No Expertise Required",
      "⚡ Upfront Cost Visibility",
      "🔄 Zero Back-and-Forth",
    ],
  },
  {
    title: "Exhaustive Catalog to Build Any Design",
    content:
      "Choose from thousands of cabinet across multiple sizes, loaded with accessories and hardwares",
    image: "exhaustive_catalog.webp", // Image: Flat-lay of various cabinet cabinets with labels
    keyPoints: [
      "📦 Ready-to-Ship Cabinets",
      "📏 Wide Range of Sizes",
      "🎨 Multiple Specifications",
      "⚙️ Hardwares Included",
    ],
  },
  {
    title: "Multiple ranges to suit your budget and spec",
    content: "Available in 3 carefully crafted ranges - Eco, Prime & Gold",
    image: "ranges.webp", // Image: Flat-lay of various cabinet cabinets with labels
    keyPoints: [
      "💰 Prices that fit your budget",
      "🎨 White & Fabric Finishes",
      "🔩 Ebco & Hettich Fittings",
      "📦 All Hardwares Included",
    ],
  },
  {
    title: "Delivered in 1 Day",
    content: "In-stock cabinets delivered in 1 day, others in 3 days.",
    image: "fast_delivery.webp", // Image: Branded ZeroBuild truck unloading flat-pack cartons at a site
    keyPoints: [
      "⏱️ Assured Delivery",
      "🚚 Wide Coverage",
      "📍 Real-Time Tracking",
      "🗓️ Complete Projects Faster",
    ],
  },
  {
    title: "Easy to Assemble",
    content:
      "All cabinets come with clear assembly drawings and labeled parts. Any local carpenter can assemble with confidence — no on-site improvisation required.",
    image: "easy_assembly.webp", // Image: Carpenter fitting panels with labeled stickers
    keyPoints: [
      "📐 Clear Assembly Drawings",
      "🏷️ Labeled Components",
      "🔧 Hassle-Free Assembly",
      "✅ Professional Finish",
    ],
  },
];

function StoreAbout() {
  const basePath = getBasePath();
  const brandName = getStoreBrandName();
  const logoPath = getStoreLogoPath();

  return (
    <div className="home-page bg-dark text-light">
      {/* Hero */}
      <section
        className="home-bg-gradient-1 pb-4 pb-md-5"
        style={{
          paddingTop: "50px",
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
        }}
      >
        <Container>
          <Row className="align-items-center">
            {/* Image Column - Hidden on mobile, visible on laptop */}
            <Col lg="6" className="d-none d-lg-block">
              <img
                src={`/images/home/hero.webp`}
                alt="Hero"
                className="img-fluid shadow"
              />
            </Col>

            {/* Text Column */}
            <Col lg="6" className="text-center text-lg-start">
              <h1
                className="fw-bold mb-2 fs-28px fs-md-50px"
                style={{ color: "#cb9b7d" }}
              >
                Readymade Carcass. <br /> Delivered in 1 Day.
              </h1>
              <h4 className="fw-bold mb-2 text-white lh-base fs-20px fs-md-30px">
                Wardrobes, Kitchen Units & More.
              </h4>
              <p className="text-light mb-5 fs-md-20px fs-16px">
                Just pick a cabinet, select a size and order.
              </p>
              <div className="d-flex justify-content-center justify-content-lg-start gap-3 mb-5">
                <Link
                  to={`${basePath}/products?src=home_browse_catalog`}
                  className="text-decoration-none"
                >
                  <Button
                    color="light"
                    className="px-sm-5 py-sm-4 fw-bold text-uppercase hero-btn-font"
                  >
                    Browse Products
                  </Button>
                </Link>
              </div>
            </Col>

            {/* Image Column - Visible only on mobile */}
            <Col lg="6" className="d-lg-none">
              <img
                src={`/images/home/hero.webp`}
                alt="Hero"
                className="img-fluid shadow"
              />
            </Col>
          </Row>
        </Container>
      </section>

      <section className="home-bg-gradient-2 py-3">
        <Container>
          <h2 className="mb-2 fw-bold fs-md-30px fs-28px text-center">
            <span className="text-uppercase" style={{ color: "#cb9b7d" }}>
              Precision
            </span>{" "}
            <span className="text-uppercase text-AAAAAA">Engineered</span>
          </h2>
          <Row className="gy-3 justify-content-center">
            {highlights.map((item, i) => (
              <Col key={i} xs="12" md="6" lg="3">
                <div className="h-100 p-4 rounded-3 text-start bg-black bg-opacity-25">
                  <div className="d-flex align-items-center mb-3 gap-2">
                    <i
                      className={`${item.icon}`}
                      style={{ fontSize: "1.5rem", color: "white" }}
                    ></i>
                    <h5 className="mb-0 fw-bold text-white fs-6">
                      {item.title}
                    </h5>
                  </div>
                  <p className="mb-0 text-white fs-6">{item.desc}</p>
                </div>
              </Col>
            ))}
          </Row>
          <div className="d-flex justify-content-center mt-5">
            <Link to={`${basePath}/products?src=home_customize`}>
              <Button color="light" className="mb-5 px-5 py-3 fs-6 fw-bold">
                Explore our Products
              </Button>
            </Link>
          </div>
        </Container>
      </section>

      {/* Features */}
      {featureSections.map((section, i) => (
        <section className={`home-bg-gradient-${i + 3} py-3 py-xl-5`} key={i}>
          <Container>
            <Row className="align-items-stretch gx-3 gx-xl-5">
              <Col md="6" className={i % 2 === 0 ? "" : "order-md-2"}>
                <img
                  src={`/images/home/features/${section.image}`}
                  alt={section.title}
                  className="img-fluid rounded-5 shadow mb-4 mb-md-0"
                />
              </Col>
              <Col
                md="6"
                className="d-flex flex-column justify-content-between text-center text-xl-start"
              >
                <div>
                  <h2
                    className="fw-bold mb-3 fs-md-45px fs-28px pt-xl-5"
                    style={{ color: "#cb9b7d" }}
                  >
                    {section.title}
                  </h2>
                  <p className="text-light fs-md-20px fs-16px">
                    {section.content}
                  </p>
                </div>
                <div className="row row-cols-2 gx-4 gy-3 mt-4 pb-xl-5">
                  {section.keyPoints.map((point, pointIndex) => (
                    <div
                      className="d-flex align-items-center gap-2 text-AAAAAA"
                      style={{ fontSize: "1rem" }}
                      key={pointIndex}
                    >
                      <span className="fw-semibold fs-md-20px">{point}</span>
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
            <hr className="w-75 mt-5 mx-auto d-xl-none" />
          </Container>
        </section>
      ))}

      {/* Browse Catalog CTA */}
      <section className="text-center py-5 p-xl-5 home-bg-gradient-10">
        <Container>
          <h2
            className="fw-bold mb-3 fs-md-50px fs-25px"
            style={{ color: "#cb9b7d" }}
          >
            Ready to Explore?
          </h2>
          <p className="text-light fs-6 mb-4">
            Browse our complete catalog of modular cabinets and find the perfect
            solutions for your projects.
          </p>
          <div className="d-flex justify-content-center">
            <Link
              to={`${basePath}/products?src=home_browse_catalog_cta`}
              className="text-decoration-none"
            >
              <Button
                color="light"
                className="px-sm-5 py-sm-3 rounded-3 d-flex align-items-center gap-2 fs-6"
              >
                Browse Catalog <i className="fa-solid fa-arrow-right"></i>
              </Button>
            </Link>
          </div>
        </Container>
      </section>

      {/* Still Got Questions CTA */}
      <section className="text-center py-5 p-xl-5 home-bg-gradient-11 border-top border-secondary">
        <Container>
          <h2
            className="fw-bold mb-3 fs-md-50px fs-25px"
            style={{ color: "#cb9b7d" }}
          >
            Still Got Questions?
          </h2>
          <p className="text-light fs-6 mb-4">
            Talk to us. We're ready to help you with any questions about our
            products.
          </p>
          <div className="d-flex justify-content-center gap-3 flex-wrap">
            <Link
              to="tel:+91-9150469996"
              className="text-decoration-none"
              onClick={() => trackGAEvent("store_home_call")}
              target="_blank"
            >
              <Button
                outline
                color="light"
                className="px-sm-5 py-sm-3 rounded-3 d-flex align-items-center gap-2 fs-6"
              >
                <i className="fa-solid fa-phone"></i> Talk to us
              </Button>
            </Link>
            <Link
              to={`https://wa.me/919150469996?text=Need%20more%20info%20about%20${encodeURIComponent(brandName)}.`}
              className="text-decoration-none"
              onClick={() => trackGAEvent("store_home_chat")}
              target="_blank"
            >
              <Button
                color="light"
                className="px-sm-5 py-sm-3 rounded-3 d-flex align-items-center gap-2 fs-6"
              >
                <i className="fa-regular fa-message"></i> Chat with Us
              </Button>
            </Link>
          </div>
        </Container>
      </section>

      {/* Footer */}
      <footer className="text-light py-2 home-bg-gradient-12 border-top border-secondary">
        <Container>
          <Row className="gy-4">
            <Col md="3">
              <div className="d-flex align-items-center mb-2">
                <img
                  src={logoPath}
                  alt={`${brandName} Logo`}
                  height={24}
                  className="me-2"
                />
              </div>
              <p className="fs-6 text-white">The Cabinet Store</p>
            </Col>
            <Col md="3">
              <div className="fs-6 text-secondary mb-2">
                <i className="fa-solid fa-location-dot"></i> Chennai
              </div>
              <a
                href="tel:+91-9150469996"
                className="fs-6 text-secondary text-decoration-none"
              >
                <i className="fa-solid fa-phone"></i> +91-915-046-9996
              </a>
            </Col>
          </Row>
        </Container>
      </footer>
    </div>
  );
}

export default StoreAbout;
